import { useEffect, useRef } from 'react';

// Lightweight scroll-reveal. Adds `is-visible` to `.reveal-up` descendants
// (and the root itself) the first time they enter the viewport.
// Pass `deps` when the set of reveal targets can change (e.g. filtered lists).
export const useReveal = ({ threshold = 0.18, stagger = 90, deps = [] } = {}) => {
  const ref = useRef(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return;

    const targets = [
      ...(root.classList.contains('reveal-up') ? [root] : []),
      ...root.querySelectorAll('.reveal-up'),
    ];
    if (targets.length === 0) return;

    // Reset visibility so re-filtered lists can animate in again.
    targets.forEach((el) => el.classList.remove('is-visible'));

    const reveal = (el, index) => {
      const delay = Math.max(0, index) * stagger;
      window.setTimeout(() => el.classList.add('is-visible'), delay);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = targets.indexOf(entry.target);
          reveal(entry.target, index);
          observer.unobserve(entry.target);
        });
      },
      { threshold, rootMargin: '0px 0px 0px 0px' }
    );

    targets.forEach((el, index) => {
      const rect = el.getBoundingClientRect();
      const inView =
        rect.top < window.innerHeight * (1 - threshold * 0.25) && rect.bottom > 0;
      if (inView) {
        reveal(el, index);
      } else {
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threshold, stagger, ...deps]);

  return ref;
};

export default useReveal;
